import { Route, Routes, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { initializeUser } from './reducers/userReducer'
import AllPlayers from './components/AllPlayers'
import Menu from './components/Menu'
import Player from './components/Player'
import Login from './components/Login'
import Callback from './components/Callback'
import MyPlayers from './components/MyPlayers'
import ListsPage from './components/ListsPage'
import TargetLevels from './components/TargetLevels'
import PlayerList from './components/PlayerList'
import PlayerCandidates from './components/PlayerCandidates'
import TransferSearch from './components/TransferSearch'
import Achievements from './components/Achievements'
import TeamTrainingPlan from './components/TeamTrainingPlan'
import Simulator from './components/Simulator'
import LeagueSimulationResults from './components/LeagueSimulationResults'
import SkillDistribution from './components/SkillDistribution'
import TeamSpiritImpact from './pages/teamSpirit/TeamSpiritImpact'
import AdminReports from './components/AdminReports'
import Testing from './components/Testing'
import { hasFinnishStaffAccess, hasTeamSpiritAccess } from './services/security'

const FinnishStaffOnlyRoute = ({ children }) => {
  const user = useSelector(state => state.user)

  if (!user || !user._loaded) {
    return null
  }

  if (!user.oauth_token) {
    return <Navigate to="/" replace />
  }

  if (!hasFinnishStaffAccess(user)) {
    return <Navigate to="/" replace />
  }

  return children
}

const TeamSpiritOnlyRoute = ({ children, isU21 }) => {
  const user = useSelector(state => state.user)

  if (!user || !user._loaded) return null
  if (!user.oauth_token || !hasTeamSpiritAccess(user, isU21)) {
    return <Navigate to="/" replace />
  }

  return children
}

const LoggedInRoute = ({ children }) => {
  const user = useSelector(state => state.user)

  if (!user || !user._loaded) {
    return null
  }

  if (!user.oauth_token) {
    return <Navigate to="/" replace />
  }

  return children
}

const AdminOnlyRoute = ({ children }) => {
  const user = useSelector(state => state.user)

  if (!user || !user._loaded) return null
  if (user.loginName !== 'Mizi91') return <Navigate to="/" replace />

  return children
}

const App = () => {
  document.body.style = 'background: #202225;'
  const dispatch = useDispatch()
  const location = useLocation()

  // Alustetaan käyttäjä Reduxiin kerran sovelluksen käynnistyessä
  useEffect(() => {
    dispatch(initializeUser())
  }, [dispatch])

  useEffect(() => {
    const path = location.pathname
    const isListRoute =
      path === '/lists' ||
      path.startsWith('/candidates/') ||
      path.startsWith('/scoutlist/')

    document.body.classList.toggle('mobile-lists-zoom', isListRoute)

    return () => {
      document.body.classList.remove('mobile-lists-zoom')
    }
  }, [location.pathname])

  return (
    <div className="container">
      <Menu />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/allplayers" element={<AllPlayers />} />
        <Route path="/players/:id" element={<Player />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login/callback" element={<Callback />} />
        <Route path="/myplayers" element={<MyPlayers />} />
        <Route path="/lists" element={<ListsPage />} />
        <Route path="/targetlevels" element={<TargetLevels />} />
        <Route path="/scoutlist/:id" element={<PlayerList />} />
        <Route path="/candidates/:id" element={<PlayerCandidates />} />
        <Route
          path="/transfers"
          element={
            <FinnishStaffOnlyRoute>
              <TransferSearch />
            </FinnishStaffOnlyRoute>
          }
        />
        <Route path="/achievements" element={<Achievements />} />
        <Route
          path="/teamtraining"
          element={
            <FinnishStaffOnlyRoute>
              <TeamTrainingPlan />
            </FinnishStaffOnlyRoute>
          }
        />
        <Route
          path="/simulator"
          element={
            <LoggedInRoute>
              <Simulator />
            </LoggedInRoute>
          }
        />
        <Route
          path="/teamspirit"
          element={
            <TeamSpiritOnlyRoute isU21={false}>
              <TeamSpiritImpact nationalTeamId={3023} isU21={false} />
            </TeamSpiritOnlyRoute>
          }
        />
        <Route
          path="/teamspirit-u21"
          element={
            <TeamSpiritOnlyRoute isU21>
              <TeamSpiritImpact nationalTeamId={3064} isU21 />
            </TeamSpiritOnlyRoute>
          }
        />
        <Route
          path="/skilldistribution"
          element={
            <LoggedInRoute>
              <SkillDistribution />
            </LoggedInRoute>
          }
        />
        <Route
          path="/skilldistribution/:playerId"
          element={
            <LoggedInRoute>
              <SkillDistribution />
            </LoggedInRoute>
          }
        />
        <Route path="/testing" element={<Testing />} />
        <Route
          path="/reports"
          element={
            <AdminOnlyRoute>
              <AdminReports />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/simulator/:matchId"
          element={
            <LoggedInRoute>
              <Simulator />
            </LoggedInRoute>
          }
        />
        <Route path="/simulator/L-:leagueId" element={<LoggedInRoute><LeagueSimulationResults /></LoggedInRoute>} />
        <Route path="/simulator/NT-:tournamentId" element={<LoggedInRoute><LeagueSimulationResults /></LoggedInRoute>} />
      </Routes>
    </div>
  )
}

export default App
